<script setup lang="ts">
  import { useDocumentStore } from '@/store/document'
  import { StyleValue, computed, inject, ref } from 'vue'
  import { UISpace } from '@gohighlevel/ghl-ui'
  import FillableFieldIndicator from '../common/FillableFieldIndicator.vue'
  import BlocksRenderer from '../common/BlocksRenderer.vue'
  import FloatingElementRenderer from '../common/FloatingElementRenderer.vue'

  import {
    IElement,
    Rotation,
    PageElementOptions,
    OLD_PAGE_HEIGHT,
    DEFAULT_PAGE_DIMENSION,
    OLD_PAGE_WIDTH,
    PAGE_SECTIONS,
  } from '@gohighlevel/ghl-proposals-common'
  import { useMobileViewV2 } from '@/composable'
  import PageSectionLoop from './PageSectionLoop.vue'

  const store = useDocumentStore()
  const isPDFView = inject<boolean>('isPDFView')
  const pageData = computed(() => store.document.pages)
  const pageParentRef = ref<HTMLElement>()
  const isPDFPage = (page: IElement<PageElementOptions>) => page?.component.options.fromPDF

  const getPageDimensions = (page: IElement<PageElementOptions>) => {
    /**
     * Page dimensions
     * @default properties from the page component
     */
    return (
      page?.component?.options?.pageDimensions || {
        dimensions: {
          width: 816,
          height: 1056,
        },
        margins: {
          top: 48,
          right: 48,
          bottom: 48,
          left: 48,
        },
        rotation: Rotation.Portrait,
      }
    )
  }

  const getPageViewMinHeight = (page: IElement<PageElementOptions>) => {
    if (page.version > 1) {
      const { height } = getPageDimensions(page).dimensions
      return height + 'px'
    }
    return isPDFPage(page) || page?.component.options.src ? `${OLD_PAGE_HEIGHT}px` : `${DEFAULT_PAGE_DIMENSION.dimensions.height}px`
  }

  const getBackgroundSize = (page: IElement<PageElementOptions>) => {
    if (isPDFPage(page)) {
      if (page.version > 1) {
        const { height, width } = getPageDimensions(page).dimensions
        return `${width}px ${height}px`
      } else {
        return `${OLD_PAGE_WIDTH}px ${OLD_PAGE_HEIGHT}px`
      }
    } else {
      return page?.responsiveStyles?.large?.backgroundSize
    }
  }

  const { scaleFactor } = useMobileViewV2()

  const getPageStyles = (page: IElement<PageElementOptions>): StyleValue => {
    return {
      backgroundColor: page?.responsiveStyles?.large?.backgroundColor || null,
      backgroundImage: `url('${page?.component?.options?.src}')`,
      backgroundPosition: page?.responsiveStyles?.large?.backgroundPosition,
      backgroundRepeat: page?.responsiveStyles?.large?.backgroundRepeat,
      backgroundSize: getBackgroundSize(page),
      opacity: page?.responsiveStyles?.large?.opacity / 100 || 1,
      zIndex: 0,
      position: 'absolute',
      width: '100%',
      height: '100%',
    }
  }

  const getDraggableElement = (children: IElement[]) => {
    if (!children) return []
    return children.filter(element => element && element?.component?.isDraggable)
  }

  const getBlockElements = (children: IElement[]) => {
    if (!children) return []
    return children.filter(element => element && !element?.component?.isDraggable)
  }

  const getHeaderSectionStyles = (page: IElement<PageElementOptions>): StyleValue => {
    const section = page?.component?.options?.[PAGE_SECTIONS.HEADERS]
    return {
      backgroundColor: section?.responsiveStyles?.large?.backgroundColor || null,
      backgroundImage: section?.component?.options?.src ? `url('${section.component.options.src}')` : null,
      backgroundPosition: section?.responsiveStyles?.large?.backgroundPosition,
      backgroundRepeat: section?.responsiveStyles?.large?.backgroundRepeat,
      backgroundSize: '100%',
      opacity: section?.responsiveStyles?.large?.opacity != null ? section.responsiveStyles.large.opacity / 100 : 1,
    }
  }

  const getFooterSectionStyles = (page: IElement<PageElementOptions>): StyleValue => {
    const section = page?.component?.options?.[PAGE_SECTIONS.FOOTERS]
    return {
      backgroundColor: section?.responsiveStyles?.large?.backgroundColor || null,
      backgroundImage: section?.component?.options?.src ? `url('${section.component.options.src}')` : null,
      backgroundPosition: section?.responsiveStyles?.large?.backgroundPosition,
      backgroundRepeat: section?.responsiveStyles?.large?.backgroundRepeat,
      backgroundSize: '100%',
      opacity: section?.responsiveStyles?.large?.opacity != null ? section.responsiveStyles.large.opacity / 100 : 1,
    }
  }
</script>

<template>
  <UISpace
    justify="center"
    :align="`center`"
    :vertical="true"
    :class="isPDFView ? '!gap-0' : '!gap-8'"
    class="relative pb-[20rem] document-container"
    :wrap="false"
    :wrap-item="false"
    :style="{
      transform: `scale(${scaleFactor})`,
      transformOrigin: 'left top',
      position: 'absolute',
    }"
  >
    <FillableFieldIndicator v-if="store.isEditing && store.hasPrevRecipientsCompleted" />
    <div
      ref="pageParentRef"
      v-for="(page, index) in pageData"
      :key="index"
      :class="`elements_page shadow-page relative ${isPDFView ? '!shadow-none !m-0' : ''}`"
    >
      <div :style="getPageStyles(page as IElement<PageElementOptions>)" />
      <div
        class="page-view"
        :style="{
          minHeight: getPageViewMinHeight(page as IElement<PageElementOptions>),
          width: getPageDimensions(page as IElement<PageElementOptions>).dimensions.width + 'px',
        }"
      >
        <div class="page-header header-footer" v-if="!isPDFView" :style="getHeaderSectionStyles(page as IElement<PageElementOptions>)">
          <div
            class="page-header-content"
            :style="{
              marginTop: '0',
              marginBottom: '0',
              marginLeft: getPageDimensions(page as IElement<PageElementOptions>).margins.left + 'px',
              marginRight: getPageDimensions(page as IElement<PageElementOptions>).margins.right + 'px',
            }"
          >
            <PageSectionLoop :section-type="PAGE_SECTIONS.HEADERS" :page="page" />
          </div>
        </div>
        <div
          class="page-content"
          :style="{
            marginLeft: getPageDimensions(page as IElement<PageElementOptions>).margins.left + 'px',
            marginRight: getPageDimensions(page as IElement<PageElementOptions>).margins.right + 'px',
          }"
        >
          <div class="document-block" style="flex: 1 1 0%; width: 100%; display: flex; flex-direction: column">
            <FloatingElementRenderer
              :elements="getDraggableElement(page.children)"
              :is-parent-page="true"
              :page-id="page.id"
              :page="page"
              :page-ref="pageParentRef"
              :parent-ref="pageParentRef"
            />
            <template v-for="(element, index) in getBlockElements(page.children)" :key="index">
              <BlocksRenderer
                :page="page"
                :page-ref="pageParentRef"
                :parent-ref="pageParentRef"
                :element="element as IElement<any>"
                :page-id="page.id as string"
                v-if="!element.component.isDraggable"
              />
            </template>
          </div>
        </div>
        <div class="page-footer header-footer" v-if="!isPDFView" :style="getFooterSectionStyles(page as IElement<PageElementOptions>)">
          <div
            class="page-footer-content"
            :style="{
              marginTop: '0',
              marginBottom: '0',
              marginLeft: getPageDimensions(page as IElement<PageElementOptions>).margins.left + 'px',
              marginRight: getPageDimensions(page as IElement<PageElementOptions>).margins.right + 'px',
            }"
          >
            <PageSectionLoop :section-type="PAGE_SECTIONS.FOOTERS" :page="page" />
          </div>
        </div>
      </div>

      <div v-if="pageData.length != index" style="page-break-before: always" />
    </div>
  </UISpace>
</template>

<style lang="scss" scoped>
  .document-container {
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    -webkit-perspective: 1000;
    perspective: 1000;
  }

  .page-view {
    position: relative;
    display: flex;
    flex-direction: column;
    contain: content;

    .pg-break {
      position: absolute;
      width: 60px;
      left: -64px;
      border-top: 2px dotted gray;
      color: rgb(141, 141, 141);
      font-size: 10px;
      padding: 2px;
      user-select: none;
      top: calc(960px + 96px);
    }
  }

  .page-header {
    .page-header-content {
      min-height: 48px;
      box-sizing: border-box;
    }
  }

  .header-footer {
    position: relative;
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
  }

  .page-footer {
    .page-footer-content {
      min-height: 48px;
      box-sizing: border-box;
    }
  }

  .elements_page {
    background: #ffffff;
    &:last-child {
      margin-bottom: 2em;
    }
  }

  .page-content {
    // margin-left: 48px;
    // margin-right: 48px;
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
  }

  .draggable-container {
    flex: 1 0 auto;
  }
</style>
